import { useState, ReactElement } from "react";
import { useParams } from "react-router-dom";
import Modal, { useCloseModal } from ".";
import scraperApi  from "api";
import { jobErrorReportUrl } from "components/hosted-scraping/job-list/utils";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { ExclamationIcon } from "@heroicons/react/outline";

import Button from "components/Button";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FullWidthLoadingSpinner } from "components/FullWidthLoadingSpinner";
import { Job, useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import Toaster from "components/Toaster";
import { formatDate } from "components/hosted-scraping/ProjectFormatDate";
import Alert from "components/Alert";
import { NewProjectConfig, countOfAllTasks } from "providers/HostedScrapingProvider/types";
import { AmazonProjectDetails, EbayProjectDetails, GoogleProjectDetails, SummaryRow, UrlProjectDetails, WalmartProjectDetails, RedfinProjectDetails, HomeDepotProjectDetails, HomeDepotProductProjectDetails, HomeDepotReviewsProjectDetails } from "components/hosted-scraping/project-summary/ProjectSummaryComponents";
import { preCalculatedRuntimeEstimation } from "components/hosted-scraping/project-summary/ProjectSummarySidebarHostedScraper";
import { schedulingInterval } from "components/hosted-scraping/showProjectConfig";
import { isAmazonProject, isAsyncUrlsProject, isEbayProject, isGoogleProject, isWalmartProject, isRedfinProject, isHomeDepotSearchProject, isHomeDepotProductProject, isHomeDepotReviewsProject, projectTypeHS, projectTypeToInputTitleHS } from "sdecontent";
import Spinner from "components/Spinner";
import useUserCounters from "hooks/useUserCounters";


type ErrorReport = {
  error: string;
  input: string;
}[];

const ErrorReportTable = ({errorReport, inputTitle}: {errorReport: ErrorReport; inputTitle: string}) => {
  return (
    <div className="flex flex-col w-full">
      <table className="table-auto w-full border shrink grow-0 bg-lightestGray dark:bg-neutral-50">
        {/* <thead className="sticky top-0 bg-white border"> */}
        <thead className="">
          <tr className="h-6">
            <th className="px-4 py-2 border">Error</th>
            <th className="px-4 py-2 border">{inputTitle}</th>
          </tr>
        </thead>
        <tbody className="overflow-y-scroll">
          {errorReport.map((line, index) => (
            <tr key={index} className="h-6">
              <td className="border px-4 py-2 h-6">{line.error}</td>
              <td className="border px-4 py-2 h-6">{line.input}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* <div className="shrink grow" /> */}
    </div>
  );
}

export function HostedScraperErrorReportModal() {
  const goBack = useCloseModal();
  const { currentProject } = useHostedScrapingProjects();
  const { publicId } = useParams() as { publicId: string; projectId: string };
  const [ inProgress, setInProgress ] = useState<boolean>(false);
  const [ errorReport, setErrorReport ] = useState<ErrorReport | undefined>(undefined);

  useEffect(() => {
    const fetchReport = async() => {
      setInProgress(true);
      try {
        const errorReportResponse = await scraperApi.hostedScraping.errorReport(publicId);
        setErrorReport(errorReportResponse);
      } finally {
        setInProgress(false);
      }
    }
    fetchReport();
  }, [publicId]);

  if (currentProject === undefined) {
    return <div>Project not found</div>;
  }
  const inputTitle = projectTypeToInputTitleHS(currentProject.config.type);

  const rawReportUrl = jobErrorReportUrl(publicId);

  return (
    <Modal headline="Error report" onClose={goBack} closeOnEscapeKey={true}>
      <div className="flex-col p-2 gap-y-12 overflow-hidden">
        <div className="grow-0 shrink flex gap-y-4 text-gray dark:text-neutral-600 overflow-auto h-96">
          {inProgress && <FullWidthLoadingSpinner />}
          {!inProgress && <ErrorReportTable inputTitle={inputTitle} errorReport={errorReport ?? []} />}
        </div>
        <div className="grow-0 shrink-0 flex justify-end items-center gap-x-4 h-14 pt-2">
          <Button text="Download raw JSON report" className="button button-secondary" href={rawReportUrl} size="MD" />
          <Button text="Close" className="button button-secondary" onClick={goBack} size="MD" />
        </div>
      </div>
    </Modal>
  );
}

/*
 * Note this is not a usual modal that is tied to a URL. It's a normal component
 * that accepts input as a property.
 */
interface IHostedScraperCancelJobModalProps {
  projectId: number;
  jobId: number;
  onClose: () => void;
}

export function HostedScraperCancelJobModal({projectId, jobId, onClose}: IHostedScraperCancelJobModalProps) {
  // const goBack = useCloseModal();
  // const { projectId, jobId } = (useParams() as unknown) as { projectId: number, jobId: number };

  const cancelJob = async () => {
    onClose();
    try {
      await scraperApi.hostedScraping.cancelJob(projectId, jobId);
      Toaster.success("Job cancelled successfully");
    } catch (err) {
      Toaster.error("Failed to cancel the job");
      console.error(err);
    }
  }

  return (
    <Modal headline="Cancel job" onClose={onClose} closeOnEscapeKey={true}>
      <div className="flex flex-col p-5 gap-y-12">
        <div className="text-gray dark:text-neutral-600">
          <span>
            Cancelling the scraping job will stop the ongoing task and may result in the loss of unsaved data. Please note that the
            credits for the successful responses of this job will be charged upon cancelation.
          </span>
        </div>
        <div className="text-gray dark:text-neutral-600 font-bold">
          <span className="font-bold">
            Are you sure you want to cancel the job?
          </span>
        </div>
        <div className="flex justify-end gap-x-4 item-center">
          <Button text="Cancel Job" className="button button-primary" onClick={cancelJob} size="MD" />
          <Button text="Close" className="button button-secondary" onClick={onClose} size="MD" />
        </div>
      </div>
    </Modal>
  );
}

/*
 * Note this is not a usual modal that is tied to a URL. It's a normal component
 * that accepts input as a property.
 *
 * The reason for this is that the usual modal gets the parameters for contexts,
 * but you don't want to have a context to show the actual job because the
 * changing job would cause the whole page to re-render.
 */
interface IHostedScraperJobInfoModalProps {
  jobList: null | Job[];
  jobId: number;
  onClose: () => void;
}

export function HostedScraperJobInfoModal({jobList, jobId, onClose}: IHostedScraperJobInfoModalProps) {

  if (jobList === null) {
    onClose();
    return null;
  }

  const job = jobList.find(j => j.id === jobId);
  if (job === undefined || job.status !== 'running') {
    onClose();
    return null;
  }

  const failedResponseCount = (job?.inProgressTasksWithAttempts || 0) + (job?.failedTasks || 0);
  const attemptsCount = (job?.asyncAttempts || 0);

  return (
    <Modal headline="Job progress details" onClose={onClose} closeOnEscapeKey={true}>
      <div className="flex flex-col p-5 gap-y-12">
        <ul className="list-disc list-inside">
          <li>Job started: <b>{formatDate(job?.createdAt)}</b></li>
          <li>Total responses to be processed: <b>{countOfAllTasks(job)}</b></li>
          <li>Completed responses: <b>{job?.successfulTasks}</b></li>
          {/* <li>Timed out responses: <b>{job?.timedoutTasks}</b></li> */}
          <li>Failed responses: <b>{failedResponseCount}</b></li>
          <li>Failed response re-attempts: <b>{attemptsCount}</b></li>
        </ul>
        {
          job?.nextRetry !== undefined && job?.nextRetry !== null
          ? (<>
            <Alert description="Our system retries Failed URLs to ensure data accuracy. This process continues until all URLs are scraped or the job times out."/>
            <ul className="list-disc list-inside">
              <li>Next Attempt Time: {formatDate(job.nextRetry)}</li>
            </ul>
          </>)
          : null
        }
        <div className="flex justify-end gap-x-4 item-center">
          <Button text="Close" className="button button-secondary" onClick={onClose} size="MD" />
        </div>
      </div>
    </Modal>
  );
}

export const SlowJobModalWarning = () => {
   return (
    <div className="flex flex-row gap-3 p-4 bg-warning-50 border border-warning-200 rounded">
      <p className="flex-1 text-base text-neutral-900">
        This job may run slow at your current concurrency.&nbsp;
        <Link to={BillingPath} className="text-primary-600 font-medium">Upgrade for faster runs.</Link>
      </p>
      <ExclamationIcon className="w-6 h-6 text-warning-600 self-center" />
    </div>
  )
}


/*
 * Note this is not a usual modal that is tied to a URL. It's a normal component
 * that accepts a project as a property.
 */
interface IHostedScraperReviewProjectModalProps {
  project: NewProjectConfig;
  cost: number | undefined;
  costInProgress: boolean;
  onStartScraping: () => void;
  onClose: () => void;
  warning?: ReactElement;
}

export function HostedScraperReviewProjectModal({project, cost, costInProgress, onStartScraping, onClose, warning}: IHostedScraperReviewProjectModalProps) {
  const userCounters = useUserCounters();
  const projectTypeTitle = projectTypeHS(project.config.type);
  const schedulingIntervalTitle = project.enabled
    ? schedulingInterval(project.scrapingInterval)
    : !project.enabled && Boolean(project.supposedToRunAt)
    ? { text: 'Once' }
    : { text: '-' };

  const inputCount = project.input.type === 'list_literal'
    ? project.input.list.split('\n').filter(l => l.trim()).length
    : 0;
  const concurrency = userCounters.counters?.concurrentRequests ?? 0;
  const concurrencyLimit = userCounters.counters?.concurrencyLimit ?? 0;
  const estimatedRunTime = preCalculatedRuntimeEstimation(inputCount, concurrencyLimit - concurrency);

  const creditPluralS = cost === 1 ? '' : 's';

  return (
    <Modal headline="Review and Start Scraping" onClose={onClose} closeOnEscapeKey={true}>
      <div className="flex flex-col p-5 gap-y-6">
        <p className="text-gray dark:text-neutral-600">
          You're about to start the scraping project. Please review your settings and click on "Start scraping" to trigger your first job.
        </p>
        <div className="p-3 bg-lightestGray dark:bg-neutral-50 text-gray dark:text-neutral-600">
          <div className="flex flex-col gap-2.5">
            <SummaryRow title="Type:" value={projectTypeTitle} />
            <SummaryRow title="Scraping frequency:" value={schedulingIntervalTitle.text} />
            { isAsyncUrlsProject(project.config.type)
              && <UrlProjectDetails collectorConfig={project.config} />}
            { isAmazonProject(project.config.type)
              && <AmazonProjectDetails collectorConfig={project.config} />}
            { isGoogleProject(project.config.type)
              && <GoogleProjectDetails collectorConfig={project.config} />}
            { isWalmartProject(project.config.type)
              && <WalmartProjectDetails collectorConfig={project.config} />}
            { isEbayProject(project.config.type)
              && <EbayProjectDetails collectorConfig={project.config} />}
            { isRedfinProject(project.config.type)
              && <RedfinProjectDetails collectorConfig={project.config} />}
            { isHomeDepotSearchProject(project.config.type)
              && <HomeDepotProjectDetails collectorConfig={project.config} />}
            { isHomeDepotProductProject(project.config.type)
              && <HomeDepotProductProjectDetails collectorConfig={project.config} />}
            { isHomeDepotReviewsProject(project.config.type)
              && <HomeDepotReviewsProjectDetails collectorConfig={project.config} />}
            {estimatedRunTime && (
              <SummaryRow title="Estimated run time:" value={estimatedRunTime} />
            )}
          </div>

          {warning}

          <div className="w-full border-t border-slate-200 my-4" />

          <div className="flex items-center justify-between">
            <p className="font-semibold">Cost per job:</p>
            {costInProgress
              ? <Spinner className="w-6 h-6 text-neutral-500 dark:text-neutral-500 animate-spin" />
              : (
                <div className="flex flex-row items-end gap-1.5">
                  <span data-testid="testEstimatedCost" className="text-5xl leading-none text-neutral-900">{cost ?? '-'}</span>
                  <span className="text-base text-neutral-500 pb-1">credit{creditPluralS}</span>
                </div>
              )
            }
          </div>
        </div>
        <div className="flex justify-end gap-x-2">
          <Button text="Cancel" className="button button-tertiary" onClick={onClose} size="MD" />
          <Button text="Start scraping" className="button button-primary" onClick={onStartScraping} size="MD" />
        </div>
      </div>
    </Modal>
  );
}
